import React, { useEffect, useRef, useState } from "react";

/**
 * FadeInOnScroll (Cinematic version)
 * Slow, smooth fade & upward motion for elegant reveal.
 */
export default function FadeInOnScroll({
  children,
  distance = 60, // stronger travel distance
  duration = 950, // slower, cinematic duration
  delay = 0,
  once = true,
  className = "",
  style = {},
  threshold = 0.15,
  rootMargin = "0px 0px -10% 0px",
}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);

  const prefersReduced =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    if (!ref.current) return;

    if (prefersReduced) {
      setVisible(true);
      return;
    }

    if (hasAnimated && once) {
      setVisible(true);
      return;
    }

    const node = ref.current;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting || entry.intersectionRatio > 0) {
            setVisible(true);
            setHasAnimated(true);
            if (once && obs) {
              try {
                obs.unobserve(node);
              } catch {}
            }
          } else if (!once) {
            setVisible(false);
          }
        });
      },
      { threshold, root: null, rootMargin }
    );

    obs.observe(node);
    return () => {
      try {
        obs.unobserve(node);
      } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, once, hasAnimated, prefersReduced]);

  const baseStyle = {
    willChange: "transform, opacity",
    transitionProperty: "transform, opacity",
    transitionDuration: `${duration}ms`,
    transitionTimingFunction: "cubic-bezier(.15,.85,.15,1)", // softer ease
    transitionDelay: `${delay}ms`,
    opacity: visible ? 1 : 0,
    transform: visible
      ? "translateY(0px) scale(1)"
      : `translateY(${distance}px) scale(0.96)`,
  };

  const reducedStyle = prefersReduced
    ? { opacity: 1, transform: "none", transition: "none" }
    : {};

  return (
    <div
      ref={ref}
      className={className}
      style={{ ...baseStyle, ...reducedStyle, ...style }}
      aria-hidden={prefersReduced ? undefined : !visible}
    >
      {children}
    </div>
  );
}

